import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Button,
  Switch,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import SchoolIcon from '@mui/icons-material/School';

import {
  FilterState,
  StatsResult,
  ChoroplethIndicator,
  CoastalStats,
  ViewTab,
  LEVEL_COLORS,
  ISLAND_REGIONS,
  COASTAL_ZONE_COLOR,
} from './types';

interface FilterSidebarProps {
  filters: FilterState;
  onIslandChange: (e: SelectChangeEvent<string>) => void;
  onRegionChange: (e: SelectChangeEvent<string>) => void;
  onDistrictChange: (e: SelectChangeEvent<string>) => void;
  onTypeToggle: (type: string) => void;
  onLevelToggle: (level: string) => void;
  onResetFilters: () => void;
  stats: StatsResult;
  regions: any[];
  districts: { code: string; name: string }[];
  choroplethIndicator: ChoroplethIndicator;
  onIndicatorChange: (indicator: ChoroplethIndicator) => void;
  activeView: ViewTab;
  showCoastalZone?: boolean;
  onCoastalZoneToggle?: () => void;
  coastalStats?: CoastalStats;
}

const TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  PUBLIC: { label: 'Government', color: '#1976D2' },
  PRIVATE: { label: 'Private', color: '#FF5722' },
};

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  filters,
  onIslandChange,
  onRegionChange,
  onDistrictChange,
  onTypeToggle,
  onLevelToggle,
  onResetFilters,
  stats,
  regions,
  districts,
  choroplethIndicator,
  onIndicatorChange,
  activeView,
  showCoastalZone,
  onCoastalZoneToggle,
  coastalStats,
}) => {
  // Filter regions based on selected island
  const filteredRegions = React.useMemo(() => {
    if (!filters.islandFilter) return regions;
    const allowedRegions = ISLAND_REGIONS[filters.islandFilter] || [];
    return regions.filter((r: any) => allowedRegions.includes(r.region_name || r.name));
  }, [regions, filters.islandFilter]);

  // Sort levels by count descending for the statistics section
  const sortedLevels = React.useMemo(() => {
    return Object.entries(stats.levels).sort(([, a], [, b]) => b - a);
  }, [stats.levels]);

  return (
    <Paper
      elevation={2}
      sx={{
        width: 280,
        overflow: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterListIcon color="primary" />
        <Typography variant="h6" fontWeight={600}>
          Filters
        </Typography>
      </Box>

      {/* Map Indicator Toggle - only visible in map view */}
      {activeView === 'map' && (
        <>
          <Box>
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              Map Indicator
            </Typography>
            <ToggleButtonGroup
              value={choroplethIndicator}
              exclusive
              onChange={(_e, value) => {
                if (value !== null) onIndicatorChange(value as ChoroplethIndicator);
              }}
              size="small"
              sx={{ display: 'flex', flexWrap: 'wrap' }}
            >
              <ToggleButton value="enrollment" sx={{ flex: 1, fontSize: '0.7rem', px: 1 }}>
                Students
              </ToggleButton>
              <ToggleButton value="teachers" sx={{ flex: 1, fontSize: '0.7rem', px: 1 }}>
                Teachers
              </ToggleButton>
              <ToggleButton value="schools" sx={{ flex: 1, fontSize: '0.7rem', px: 1 }}>
                Schools
              </ToggleButton>
              <ToggleButton value="ptr" sx={{ flex: 1, fontSize: '0.7rem', px: 1 }}>
                PTR
              </ToggleButton>
              <ToggleButton value="gpi" sx={{ flex: 1, fontSize: '0.7rem', px: 1 }}>
                GPI
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {/* Coastal zone layer toggle */}
          {onCoastalZoneToggle && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Map Layers
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={showCoastalZone ?? false}
                    onChange={onCoastalZoneToggle}
                    size="small"
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: COASTAL_ZONE_COLOR } }}
                  />
                }
                label={<Typography variant="body2">Coastal Zone (5km)</Typography>}
              />
            </Box>
          )}

          <Divider />
        </>
      )}

      {/* Location Filters */}
      <Box>
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
          Location
        </Typography>

        {/* Island */}
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel>Island</InputLabel>
          <Select
            value={filters.islandFilter}
            onChange={onIslandChange}
            label="Island"
          >
            <MenuItem value="">All Zanzibar</MenuItem>
            <MenuItem value="Unguja">Unguja</MenuItem>
            <MenuItem value="Pemba">Pemba</MenuItem>
          </Select>
        </FormControl>

        {/* Region */}
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel>Region</InputLabel>
          <Select
            value={filters.regionFilter}
            onChange={onRegionChange}
            label="Region"
          >
            <MenuItem value="">All Regions</MenuItem>
            {filteredRegions.map((r: any) => (
              <MenuItem key={r.region_id || r.code || r.region_name} value={r.region_name || r.name}>
                {r.region_name || r.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* District */}
        <FormControl fullWidth size="small">
          <InputLabel>District</InputLabel>
          <Select
            value={filters.districtFilter}
            onChange={onDistrictChange}
            label="District"
          >
            <MenuItem value="">All Districts</MenuItem>
            {districts.map((d) => (
              <MenuItem key={d.code} value={d.code}>
                {d.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider />

      {/* School Type */}
      <Box>
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
          School Type
        </Typography>
        <FormGroup>
          {Object.entries(TYPE_CONFIG).map(([type, config]) => (
            <FormControlLabel
              key={type}
              control={
                <Checkbox
                  checked={filters.typeFilters[type] ?? true}
                  onChange={() => onTypeToggle(type)}
                  sx={{
                    color: config.color,
                    '&.Mui-checked': { color: config.color },
                  }}
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{config.label}</Typography>
                  <Chip
                    label={type === 'PUBLIC' ? stats.government : stats.private}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              }
            />
          ))}
        </FormGroup>
      </Box>

      <Divider />

      {/* School Level */}
      <Box>
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
          School Level
        </Typography>
        <FormGroup>
          {['Pre-Primary', 'Primary', 'Secondary', 'Basic'].map((level) => (
            <FormControlLabel
              key={level}
              control={
                <Checkbox
                  checked={filters.levelFilters[level] ?? true}
                  onChange={() => onLevelToggle(level)}
                  sx={{
                    color: LEVEL_COLORS[level] || '#757575',
                    '&.Mui-checked': { color: LEVEL_COLORS[level] || '#757575' },
                  }}
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{level}</Typography>
                  <Chip
                    label={stats.levels[level] || 0}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              }
            />
          ))}
        </FormGroup>
      </Box>

      <Divider />

      {/* Reset Button */}
      <Button
        variant="outlined"
        startIcon={<RefreshIcon />}
        onClick={onResetFilters}
        fullWidth
        size="small"
      >
        Reset All Filters
      </Button>

      <Divider />

      {/* Statistics */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <SchoolIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600}>
            Statistics
          </Typography>
        </Box>

        {/* School counts */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Total Schools
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {stats.total.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Government
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ color: '#1976D2' }}>
              {stats.government.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Private
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ color: '#FF5722' }}>
              {stats.private.toLocaleString()}
            </Typography>
          </Box>
        </Box>

        {/* Level counts sorted descending */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
          {sortedLevels.map(([level, count]) => (
            <Box key={level} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                {level}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: LEVEL_COLORS[level] || '#757575' }}
              >
                {count.toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Students */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Total Students
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {stats.totalStudents.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Male
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {stats.maleStudents.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Female
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {stats.femaleStudents.toLocaleString()}
            </Typography>
          </Box>
        </Box>

        {/* Teachers */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Total Teachers
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {stats.totalTeachers.toLocaleString()}
          </Typography>
        </Box>

        {/* PTR */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            PTR
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {stats.ptr > 0 ? `${stats.ptr}:1` : 'N/A'}
          </Typography>
        </Box>

        {/* GPI */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            GPI
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {stats.gpi !== null && stats.gpi !== undefined ? stats.gpi.toFixed(2) : 'N/A'}
          </Typography>
        </Box>

        {/* Coastal Zone Stats */}
        {coastalStats && coastalStats.schoolCount > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5, color: COASTAL_ZONE_COLOR }}>
              Coastal Zone (5km)
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
              <Typography variant="body2" color="text.secondary">
                Schools
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color: COASTAL_ZONE_COLOR }}>
                {coastalStats.schoolCount.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
              <Typography variant="body2" color="text.secondary">
                Students
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {coastalStats.totalStudents.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                % of GPS Schools
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {coastalStats.percentOfAll}%
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default FilterSidebar;
